import React, { useState, useEffect, useContext } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Link, withRouter, RouteComponentProps } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Field, reduxForm, InjectedFormProps } from 'redux-form';
import Button from '@omilia/button';
import { trim, isEmpty } from 'lodash';

// @ts-ignore
import { getIsLoading, getError } from 'store/services/session/selectors';
// @ts-ignore
import { loginUser } from 'store/services/session/actions';
import { SessionRootState } from 'store/services/session/types';
import Layout from 'components/AuthLayout';
import TextFieldReduxWrapper from 'components/TextFieldReduxWrapper';
// @ts-ignore
import MESSAGES from 'constants/messages';
import FORM_NAMES from 'constants/formNames';
import { KeycloakActionsContext } from 'hooks/useKeycloak';
import validate from './utils';
import { SH1, SExtraSection, SExtraSectionText, SParagraph } from './styled';

type FormParams = {
  email: string;
  password: string;
};

interface StateProps {
  isLoading: boolean;
  loginError: string;
}

interface DispatchProps {
  loginUser: (data: FormParams) => Promise<boolean>;
}

type OwnProps = {
  hasKeycloak: boolean;
};

type CustomProps = StateProps & DispatchProps & RouteComponentProps<{}>;

type Props = OwnProps &
  CustomProps &
  InjectedFormProps<FormParams, CustomProps>;

const Login = ({
  isLoading,
  invalid,
  handleSubmit,
  loginError,
  loginUser,
  history,
  hasKeycloak,
}: Props) => {
  const [loginAction, setLoginAction] = useState(false);
  const { login, setKeycloakEnabled } = useContext(KeycloakActionsContext);

  useEffect(() => {
    if (loginAction && isEmpty(loginError)) {
      // TODO: Uncomment line bellow and check if it works properly after project slug feature addition
      // User will redirect to page pasted on url before login if any otherwise to '/'
      // pathToRedirect = _get(this.props.location, 'state.from', '/');
      history.push('/');
    }
    return () => setLoginAction(false);
  }, [loginAction, loginError, history]);

  const handleSubmitForm = async (data: FormParams) => {
    await loginUser(data);
    setLoginAction(true);
  };

  const handleKeycloakLogin = () => {
    setKeycloakEnabled();
    login();
  };

  return (
    <Layout onSubmit={handleSubmit(handleSubmitForm)}>
      <Helmet>
        <title>Log in to your account</title>
      </Helmet>
      <SH1 data-test="title">Log in to your account</SH1>
      <Field
        autoComplete="off"
        component={TextFieldReduxWrapper}
        data-test="email"
        label="Email"
        name="email"
        normalize={trim}
        placeholder={MESSAGES.LOGIN.EMAIL.PLACEHOLDER}
        type="email"
      />
      <Field
        component={TextFieldReduxWrapper}
        data-test="password"
        label="Password"
        name="password"
        normalize={trim}
        placeholder={MESSAGES.LOGIN.PASSWORD.PLACEHOLDER}
        type="password"
      />
      <SExtraSection>
        <Link data-test="forgot-link" to="/password/new">
          <SExtraSectionText level={4}>Forgot your password?</SExtraSectionText>
        </Link>
      </SExtraSection>
      <Button
        block
        color="primary"
        data-test="submit"
        disabled={invalid || isLoading}
        loading={isLoading}
        type="submit"
        variant="contained"
      >
        Log in
      </Button>
      {hasKeycloak && (
        <>
          <SParagraph>or</SParagraph>
          <Button
            block
            color="primary"
            data-test="keycloak-submit"
            variant="contained"
            onClick={handleKeycloakLogin}
          >
            Log in with omAccount
          </Button>
        </>
      )}
    </Layout>
  );
};

const mapStateToProps = (
  state: Pick<SessionRootState, 'login'>,
): StateProps => ({
  isLoading: getIsLoading(state),
  loginError: getError(state),
});

export default compose(
  withRouter,
  connect(mapStateToProps, { loginUser }),
  reduxForm({
    form: FORM_NAMES.USER.LOGIN,
    validate,
    touchOnChange: true,
    initialValues: { email: '', password: '' },
  }),
)(Login) as React.FC<OwnProps>;
